import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconView from "./IconView";
import Divider from "./Divider";
import { BuildContext } from "../classes/BuildContext";

type _HeadeViewOnTap = (d3eState: CollapseItemViewRefs) => void;

export interface CollapseItemViewProps extends BaseUIProps {
  key?: string;
  title: string;
  child?: ReactNode;
}
/// To store state data for CollapseItemView
class CollapseItemViewRefs {
  public headeView: HeadeViewState = new HeadeViewState();
}

interface HeadeViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: CollapseItemViewRefs;
  _onChangeCollapseRight?: _HeadeViewOnTap;
  expanded: boolean;
  title: string;
}

class HeadeViewState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _HeadeViewWithState extends ObservableComponent<HeadeViewWithStateProps> {
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: HeadeViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get expanded(): boolean {
    return this.props.expanded;
  }
  public get headeView(): HeadeViewState {
    return this.props.d3eState.headeView;
  }
  public get title(): string {
    return this.props.title;
  }
  public get d3eState(): CollapseItemViewRefs {
    return this.props.d3eState;
  }
  public get _onChangeCollapseRight(): _HeadeViewOnTap {
    return this.props._onChangeCollapseRight;
  }
  public initState() {
    super.initState();

    this.updateObservable("headeView", null, this.headeView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["expanded", "headeView", "headeView.hover", "title"],
      this.rebuild
    );
  }
  public headeViewOnEnter(event): void {
    return this.headeView.setHover(true);
  }
  public headeViewOnExit(event): void {
    return this.headeView.setHover(false);
  }
  public dispose(): void {
    this.headeView.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
      states: ui.joinStates({ "data-c0": this.headeView.hover }, {}),
      children: [
        TextView({
          data: this.title,
          style: new ui.TextStyle({
            fontWeight: ui.FontWeight.bold,
            fontSize: 13.0,
          }),
          className: "xe1",
          key: "0",
        }),
        this.expanded
          ? IconView({
              icon: MaterialIcons.keyboard_arrow_down,
              color: cStyle.c3,
              className: "xd0",
            })
          : IconView({
              icon: MaterialIcons.keyboard_arrow_right,
              color: cStyle.c3,
              className: "xe9",
            }),
      ],
      onEnter: (event) => {
        this.headeViewOnEnter(event);
      },
      onExit: (event) => {
        this.headeViewOnExit(event);
      },
      onTap: (e) => {
        e.stopPropagation();

        this._onChangeCollapseRight(this.d3eState);
      },
      className: "x63 hc h",
    });
  }
}
function HeadeViewWithState(props: HeadeViewWithStateProps) {
  return React.createElement(_HeadeViewWithState, props);
}

class _CollapseItemViewState extends ObservableComponent<CollapseItemViewProps> {
  static defaultProps = { title: "" };
  d3eState: CollapseItemViewRefs = new CollapseItemViewRefs();
  expanded: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CollapseItemViewProps) {
    super(props);

    this.initState();
  }
  public get title(): string {
    return this.props.title;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["expanded", "title"], this.rebuild);
  }
  public componentDidUpdate(prevProps: CollapseItemViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.title !== this.props.title) {
      this.fire("title", this);
    }
  }
  public setExpanded(val: boolean): void {
    let isValChanged: boolean = this.expanded !== val;

    if (!isValChanged) {
      return;
    }

    this.expanded = val;

    this.fire("expanded", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        HeadeViewWithState({
          d3eState: this.d3eState,
          _onChangeCollapseRight: this.onChangeCollapseRight,
          expanded: this.expanded,
          title: this.title,
          key: "0",
        }),
        Divider({
          margin: ui.EdgeInsets.zero,
          className: "xa1f hc h",
          key: "1",
        }),
        this.expanded ? this.props.child : [],
      ],
      className: ui.join(this.props.className, "CollapseItemView xf03 hc h"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onChangeCollapseRight = (d3eState: CollapseItemViewRefs): void => {
    this.setExpanded(!this.expanded);
  };
  public get childPresent(): boolean {
    return this.props.child != null;
  }
  public get child(): ReactNode {
    return this.props.child;
  }
  public get headeView() {
    return this.d3eState.headeView;
  }
}
export default function CollapseItemView(props: CollapseItemViewProps) {
  return React.createElement(_CollapseItemViewState, {
    ..._CollapseItemViewState.defaultProps,
    ...props,
  });
}
